import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Minus, CreditCard, ShieldAlert, Info, Crown, ChevronRight } from 'lucide-react';

export default function CancelSubscriptionModal({
    isOpen,
    onClose,
    subscription,
    onConfirm,
    processing
}) {
    const [cancellationReason, setCancellationReason] = useState('');
    const [otherFeedback, setOtherFeedback] = useState('');

    if (!isOpen) return null;

    const reasons = [
        { id: 'expensive', label: 'Pricing concern', icon: '💰' },
        { id: 'project-ended', label: 'Project completed', icon: '✅' },
        { id: 'missing-features', label: 'Missing features', icon: '🔧' },
        { id: 'other', label: 'Other reasons', icon: '💬' }
    ];

    const handleSubmit = () => {
        onConfirm({ reason: cancellationReason, feedback: otherFeedback });
    };

    const lostFeatures = [
        { title: 'Basic Search Limit', desc: 'Reverts to 2 property searches per month.' },
        { title: 'LiDAR & GIS Map View', desc: 'Elevation contour graphs and parcel boundary polygons locked.' },
        { title: 'Custom Reports & Exports', desc: 'PDF report downloads and comparison history logs disabled.' },
        { title: 'Premium Analysis Formulas', desc: 'Advanced LUF, MDA, and MFA calculations disabled.' },
    ];

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-5 md:p-6 overflow-y-auto">
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 backdrop-blur-md"
                style={{ background: 'linear-gradient(135deg, rgba(30,27,75,0.45) 0%, rgba(88,28,135,0.35) 50%, rgba(136,19,55,0.30) 100%)' }}
                onClick={onClose}
            />

            {/* Modal Box */}
            <motion.div
                initial={{ scale: 0.92, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.92, opacity: 0, y: 20 }}
                transition={{ type: 'spring', damping: 28, stiffness: 340 }}
                className="relative max-w-[420px] sm:max-w-lg lg:max-w-[1020px] w-full rounded-[20px] sm:rounded-[24px] lg:rounded-[28px] overflow-hidden flex flex-col lg:flex-row z-10 my-auto max-h-[92vh] lg:max-h-[90vh]"
                style={{
                    boxShadow: '0 40px 100px -20px rgba(30,27,75,0.35), 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.06)'
                }}
            >
                {/* Close Button — right panel (light bg) */}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 sm:top-5 sm:right-5 lg:top-6 lg:right-6 z-20 p-1.5 sm:p-2 rounded-lg sm:rounded-xl transition-all duration-300 cursor-pointer group"
                    style={{
                        background: 'rgba(30,27,75,0.06)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(30,27,75,0.08)'
                    }}
                >
                    <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-all duration-300 group-hover:rotate-90" style={{ color: '#64748b' }} />
                </button>

                {/* ════════════════════════════════════════════════════════════════
                    LEFT PANEL — Deep Indigo / Violet Gradient
                   ════════════════════════════════════════════════════════════════ */}
                <div
                    className="hidden lg:block lg:w-[48%] relative overflow-hidden"
                    style={{
                        background: 'linear-gradient(165deg, #1e1b4b 0%, #312e81 30%, #4c1d95 60%, #581c87 100%)'
                    }}
                >
                    {/* Floating gradient orbs for depth */}
                    <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-20 blur-3xl pointer-events-none"
                        style={{ background: 'radial-gradient(circle, #818cf8 0%, transparent 70%)' }} />
                    <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-15 blur-3xl pointer-events-none"
                        style={{ background: 'radial-gradient(circle, #a78bfa 0%, transparent 70%)' }} />
                    <div className="absolute top-1/2 right-0 w-32 h-32 rounded-full opacity-10 blur-2xl pointer-events-none"
                        style={{ background: 'radial-gradient(circle, #c4b5fd 0%, transparent 70%)' }} />

                    <div className="relative z-10 p-7 sm:p-8 lg:p-10 flex flex-col justify-between h-full">
                        <div>
                            {/* Badge */}
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.15 }}
                                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] mb-5"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(167,139,250,0.2) 0%, rgba(129,140,248,0.15) 100%)',
                                    border: '1px solid rgba(167,139,250,0.25)',
                                    color: '#c4b5fd'
                                }}
                            >
                                <Crown className="w-3 h-3" />
                                Premium Benefits You'll Lose
                            </motion.div>

                            {/* Title */}
                            <motion.h4
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="text-[22px] sm:text-2xl font-extrabold tracking-tight mb-2 leading-tight"
                                style={{ color: '#e0e7ff' }}
                            >
                                Downgrade Impact
                            </motion.h4>
                            <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.25 }}
                                className="text-[11px] sm:text-xs font-medium mb-5 leading-relaxed"
                                style={{ color: 'rgba(196,181,253,0.6)' }}
                            >
                                The following premium features will revert to the Basic tier limits upon confirmation:
                            </motion.p>

                            {/* Feature Loss Cards */}
                            <div className="space-y-2">
                                {lostFeatures.map((feature, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.3 + i * 0.08 }}
                                        className="rounded-2xl p-3.5 flex items-start gap-3 group/card transition-all duration-300 hover:scale-[1.01]"
                                        style={{
                                            background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
                                            border: '1px solid rgba(255,255,255,0.07)',
                                            backdropFilter: 'blur(8px)'
                                        }}
                                    >
                                        <div
                                            className="rounded-xl p-2 flex-shrink-0 transition-all duration-300"
                                            style={{
                                                background: 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.1) 100%)',
                                                border: '1px solid rgba(239,68,68,0.15)'
                                            }}
                                        >
                                            <Minus className="w-3 h-3" style={{ color: '#fca5a5' }} />
                                        </div>
                                        <div>
                                            <h5 className="text-[11px] sm:text-xs font-bold mb-0.5" style={{ color: '#e0e7ff' }}>
                                                {feature.title}
                                            </h5>
                                            <p className="text-[10px] font-medium leading-relaxed" style={{ color: 'rgba(196,181,253,0.5)' }}>
                                                {feature.desc}
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Cancellation Reason — moved here to fill left panel space */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.65 }}
                            className="mt-5 pt-5"
                            style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
                        >
                            <p className="text-[10px] font-bold uppercase tracking-[0.1em] mb-3"
                                style={{ color: 'rgba(196,181,253,0.5)' }}>
                                Reason for leaving
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                {reasons.map((reason) => (
                                    <button
                                        key={reason.id}
                                        type="button"
                                        onClick={() => setCancellationReason(reason.id)}
                                        className="p-2.5 sm:p-3 rounded-xl text-[10px] sm:text-[11px] text-left font-semibold transition-all duration-300 cursor-pointer flex items-center gap-2"
                                        style={{
                                            background: cancellationReason === reason.id
                                                ? 'linear-gradient(135deg, rgba(167,139,250,0.15) 0%, rgba(129,140,248,0.1) 100%)'
                                                : 'rgba(255,255,255,0.04)',
                                            border: cancellationReason === reason.id
                                                ? '1px solid rgba(167,139,250,0.35)'
                                                : '1px solid rgba(255,255,255,0.07)',
                                            color: cancellationReason === reason.id ? '#c4b5fd' : 'rgba(196,181,253,0.5)',
                                            transform: cancellationReason === reason.id ? 'scale(1.02)' : 'scale(1)'
                                        }}
                                    >
                                        <span className="text-xs">{reason.icon}</span>
                                        {reason.label}
                                    </button>
                                ))}
                            </div>

                            {/* Feedback textarea */}
                            <AnimatePresence>
                                {cancellationReason && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <textarea
                                            rows={2}
                                            value={otherFeedback}
                                            onChange={(e) => setOtherFeedback(e.target.value)}
                                            placeholder={cancellationReason === 'other' ? "Please tell us more..." : "Anything else? (Optional)"}
                                            className="w-full text-[11px] p-3 rounded-xl focus:outline-none resize-none font-medium mt-2"
                                            style={{
                                                background: 'rgba(255,255,255,0.06)',
                                                border: '1px solid rgba(255,255,255,0.08)',
                                                color: '#e0e7ff',
                                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
                                                '::placeholder': { color: 'rgba(196,181,253,0.3)' }
                                            }}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    </div>
                </div>

                {/* ════════════════════════════════════════════════════════════════
                    RIGHT PANEL — Warm Rose / Blush / Cream Tint
                   ════════════════════════════════════════════════════════════════ */}
                <div
                    className="w-full lg:w-[52%] relative overflow-hidden min-h-0 overflow-y-auto"
                    style={{
                        background: 'linear-gradient(175deg, #fffbf7 0%, #fef7f0 25%, #fdf2f8 55%, #fce7f3 100%)'
                    }}
                >
                    {/* Subtle decorative orb */}
                    <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-30 blur-3xl pointer-events-none"
                        style={{ background: 'radial-gradient(circle, #fecdd3 0%, transparent 70%)' }} />
                    <div className="absolute bottom-0 left-1/4 w-40 h-40 rounded-full opacity-20 blur-3xl pointer-events-none"
                        style={{ background: 'radial-gradient(circle, #e9d5ff 0%, transparent 70%)' }} />

                    <div className="relative z-10 p-4 sm:p-6 lg:p-10 flex flex-col justify-between h-full text-left">
                        <div>
                            {/* Title */}
                            <motion.h3
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.15 }}
                                className="text-lg sm:text-xl lg:text-2xl font-extrabold tracking-tight leading-none mb-1.5 pr-8"
                                style={{ color: '#1e1b4b' }}
                            >
                                Cancel Subscription
                            </motion.h3>
                            <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="text-[10px] sm:text-[11px] lg:text-xs font-medium mb-4 sm:mb-5 lg:mb-6"
                                style={{ color: '#94a3b8' }}
                            >
                                Review your billing details before confirming cancellation.
                            </motion.p>

                            {/* Two Columns for Info and Policy */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.25 }}
                                className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-3.5 mb-4 sm:mb-5 lg:mb-6"
                            >
                                {/* Billing Info */}
                                <div
                                    className="rounded-xl sm:rounded-2xl p-3.5 sm:p-4 lg:p-5 space-y-2.5 sm:space-y-3.5"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.6) 100%)',
                                        border: '1px solid rgba(226,232,240,0.6)',
                                        backdropFilter: 'blur(12px)',
                                        boxShadow: '0 4px 24px -4px rgba(15,23,42,0.04), 0 1px 2px rgba(0,0,0,0.02)'
                                    }}
                                >
                                    <h4 className="text-[10px] font-bold uppercase tracking-[0.1em] flex items-center gap-2 mb-1"
                                        style={{ color: '#94a3b8' }}>
                                        <CreditCard className="w-3.5 h-3.5" style={{ color: '#a78bfa' }} />
                                        Billing Summary
                                    </h4>
                                    <div className="flex justify-between items-center text-xs pb-2"
                                        style={{ borderBottom: '1px solid rgba(226,232,240,0.5)' }}>
                                        <span className="font-medium" style={{ color: '#64748b' }}>Plan</span>
                                        <span className="font-bold text-xs px-2.5 py-0.5 rounded-lg"
                                            style={{ color: '#4c1d95', background: 'rgba(139,92,246,0.08)' }}>
                                            {subscription?.plan?.name || 'Standard Plan'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs pb-2"
                                        style={{ borderBottom: '1px solid rgba(226,232,240,0.5)' }}>
                                        <span className="font-medium" style={{ color: '#64748b' }}>Renews Until</span>
                                        <span className="font-semibold text-[11px]" style={{ color: '#334155' }}>
                                            {subscription?.current_period_end
                                                ? new Date(subscription.current_period_end).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                                                : '—'}
                                        </span>
                                    </div>

                                    <div className="flex justify-between items-center text-xs">
                                        <span className="font-medium" style={{ color: '#64748b' }}>Remaining</span>
                                        <span
                                            className="text-[10px] font-bold px-2.5 py-1 rounded-full"
                                            style={{
                                                color: '#b45309',
                                                background: 'linear-gradient(135deg, rgba(251,191,36,0.12) 0%, rgba(245,158,11,0.08) 100%)',
                                                border: '1px solid rgba(251,191,36,0.15)'
                                            }}
                                        >
                                            {(() => {
                                                if (!subscription?.current_period_end) return '0 days';
                                                const end = new Date(subscription.current_period_end);
                                                const now = new Date();
                                                const diffTime = end - now;
                                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                                return diffDays > 0 ? `${diffDays} days` : '0 days';
                                            })()}
                                        </span>
                                    </div>
                                </div>

                                {/* Policy Info */}
                                <div
                                    className="rounded-xl sm:rounded-2xl p-3.5 sm:p-4 lg:p-5 space-y-2 sm:space-y-3"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.6) 100%)',
                                        border: '1px solid rgba(226,232,240,0.6)',
                                        backdropFilter: 'blur(12px)',
                                        boxShadow: '0 4px 24px -4px rgba(15,23,42,0.04), 0 1px 2px rgba(0,0,0,0.02)'
                                    }}
                                >
                                    <h4 className="text-[10px] font-bold uppercase tracking-[0.1em] flex items-center gap-2 mb-1"
                                        style={{ color: '#94a3b8' }}>
                                        <Info className="w-3.5 h-3.5" style={{ color: '#fb923c' }} />
                                        Billing Policy
                                    </h4>
                                    <div className="space-y-1.5">
                                        <p className="text-[9px] font-bold uppercase tracking-[0.08em]" style={{ color: '#475569' }}>
                                            Reactivate Anytime
                                        </p>
                                        <p className="text-[10px] sm:text-[11px] font-medium leading-relaxed" style={{ color: '#94a3b8' }}>
                                            You can reactivate your subscription instantly to restore full parcel analysis histories and team data.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Warning Box */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.35 }}
                                className="rounded-xl sm:rounded-2xl p-3.5 sm:p-4 lg:p-5"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(245,158,11,0.05) 100%)',
                                    border: '1px solid rgba(251,191,36,0.15)',
                                    boxShadow: '0 4px 16px -4px rgba(245,158,11,0.06)'
                                }}
                            >
                                <div className="flex items-start gap-3.5 mb-3">
                                    <div className="p-2 rounded-xl flex-shrink-0"
                                        style={{
                                            background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(217,119,6,0.1) 100%)',
                                            border: '1px solid rgba(245,158,11,0.15)'
                                        }}>
                                        <ShieldAlert className="w-3.5 h-3.5" style={{ color: '#d97706' }} />
                                    </div>
                                    <div className="space-y-0.5">
                                        <h5 className="text-xs font-bold" style={{ color: '#92400e' }}>
                                            Deactivation will occur immediately
                                        </h5>
                                        <p className="text-[10px] sm:text-[11px] font-medium leading-relaxed" style={{ color: '#b45309' }}>
                                            Please be aware of the following consequences before proceeding:
                                        </p>
                                    </div>
                                </div>
                                <ul className="space-y-1 sm:space-y-1.5 pl-9 sm:pl-[42px]">
                                    <li className="flex items-start gap-2 text-[10px] sm:text-[11px] font-medium leading-relaxed" style={{ color: '#b45309' }}>
                                        <span style={{ color: '#d97706' }}>•</span>
                                        Saved project data and search history will be retained for 30 days, then permanently deleted.
                                    </li>
                                    <li className="flex items-start gap-2 text-[10px] sm:text-[11px] font-medium leading-relaxed" style={{ color: '#b45309' }}>
                                        <span style={{ color: '#d97706' }}>•</span>
                                        Any active team members linked to your account will lose shared workspace access immediately.
                                    </li>
                                    <li className="flex items-start gap-2 text-[10px] sm:text-[11px] font-medium leading-relaxed" style={{ color: '#b45309' }}>
                                        <span style={{ color: '#d97706' }}>•</span>
                                        Re-subscribing later may be subject to updated pricing and won't restore deleted data.
                                    </li>
                                </ul>
                            </motion.div>
                        </div>

                        {/* Action Buttons */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.45 }}
                            className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 pt-4 sm:pt-5 mt-4 sm:mt-6"
                            style={{ borderTop: '1px solid rgba(226,232,240,0.4)' }}
                        >
                            <button
                                onClick={onClose}
                                className="flex-1 order-2 sm:order-1 py-3 sm:py-3.5 lg:py-4 px-5 font-bold text-[11px] sm:text-xs lg:text-[13px] rounded-xl sm:rounded-2xl transition-all duration-300 text-center cursor-pointer flex items-center justify-center gap-2 group"
                                style={{
                                    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
                                    color: '#e0e7ff',
                                    boxShadow: '0 8px 32px -8px rgba(30,27,75,0.4), 0 2px 8px rgba(30,27,75,0.15), inset 0 1px 0 rgba(255,255,255,0.08)',
                                    border: '1px solid rgba(99,102,241,0.2)'
                                }}
                            >
                                <Sparkles className="w-3.5 h-3.5 group-hover:rotate-12 transition-transform duration-300" />
                                Keep Premium Plan
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={processing}
                                className="flex-1 order-1 sm:order-2 py-3 sm:py-3.5 lg:py-4 px-5 font-semibold text-[11px] sm:text-xs lg:text-[13px] rounded-xl sm:rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer group"
                                style={{
                                    background: 'rgba(255,255,255,0.65)',
                                    border: '1px solid rgba(226,232,240,0.5)',
                                    color: '#94a3b8',
                                    backdropFilter: 'blur(8px)',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                }}
                                onMouseEnter={(e) => {
                                    if (!processing) {
                                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(254,226,226,0.5) 0%, rgba(252,231,243,0.4) 100%)';
                                        e.currentTarget.style.borderColor = 'rgba(244,63,94,0.25)';
                                        e.currentTarget.style.color = '#e11d48';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.65)';
                                    e.currentTarget.style.borderColor = 'rgba(226,232,240,0.5)';
                                    e.currentTarget.style.color = '#94a3b8';
                                }}
                            >
                                {processing ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin"></div>
                                        <span>Cancelling...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Cancel Subscription</span>
                                        <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-300" />
                                    </>
                                )}
                            </button>
                        </motion.div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
